import React, { Component } from 'react'
import { navigate } from '@reach/router'

import { Table, Header, ListFilters, Page } from 'components'
import { TopicApi, PackApi } from 'api'
import { pages, operations, ButtonProps } from 'types'
import { topicEdit, addtopic } from 'utils/endpoints'
import { isSuperAdmin } from 'utils/permission'
import Spinner from 'react-bootstrap/Spinner'
import { matchesListSearch } from 'utils/listFilters'
import { getListFilters, persistListFilters } from 'utils/listViewState'

interface Props {
  path: string
}

interface State {
  loading: boolean
  questionCountFilter: string
  search: string
}
export default class TopicsPage extends Component<Props, State> {
  state: State = {
    loading: true,
    ...getListFilters(pages.TOPIC_LIST, {
      questionCountFilter: 'all',
      search: '',
    }),
  }
  page?: Page
  allPacks = new Map()
  topics?: any

  async componentDidMount() {
    try {
      const [allPacks, topics] = await Promise.all([PackApi.getPacksLight(), TopicApi.getTopics()])
      allPacks.forEach((pack: any) => {
        this.allPacks.set(pack._id, pack.name)
      })
      this.topics = topics
      this.setState({ loading: false })
    } catch (e) {}
  }

  componentDidUpdate(_previousProps: Props, previousState: State) {
    const { questionCountFilter, search } = this.state
    if (previousState.questionCountFilter !== questionCountFilter || previousState.search !== search) {
      persistListFilters(pages.TOPIC_LIST, { questionCountFilter, search })
    }
  }

  refreshTopics = async () => {
    try {
      const topics = await TopicApi.getTopics()
      this.topics = topics
      this.setState({ loading: false })
    } catch (e) {}
  }

  onEdit = (topicId: string) => {
    navigate(topicEdit + topicId)
  }

  getKeysAndValues = () => {
    const keys = ['Nome', 'N° domande']
    const rows: { id: any; values: string[]; operations?: operations[] }[] = []

    this.allPacks.forEach((pack) => {
      keys.push(pack)
    })

    for (let key in this.topics) {
      let packsCount: any[] = Object.values(this.topics[key].packs)
      packsCount = packsCount.sort((a, b) => {
        return a.order - b.order
      })
      let total = 0
      packsCount = packsCount.map((p) => p.count)
      packsCount.forEach((p) => (total += p))
      const matchesSearch = matchesListSearch(this.state.search, [this.topics[key].name, this.topics[key].description])
      const matchesQuestionCount =
        this.state.questionCountFilter === 'all' ||
        (this.state.questionCountFilter === 'withQuestions' && total > 0) ||
        (this.state.questionCountFilter === 'empty' && total === 0)
      if (!matchesSearch || !matchesQuestionCount) continue

      const values: string[] = ['' + this.topics[key].name].concat([total.toString()]).concat(packsCount)

      const operations: operations[] = []
      const topicIsEmpty = total === 0
      if (isSuperAdmin()) operations.push('Modifica')
      if (isSuperAdmin() && topicIsEmpty) operations.push('Cancella')

      rows.push({
        id: key,
        values,
        operations: operations,
      })
    }
    return { keys, rows }
  }

  askDelete = (topicId: string) => {
    this.page?.showConfirmDialog('Cancellazione materia', 'Vuoi davvero cancellare questo materia?', async () => {
      await TopicApi.deleteTopic(topicId)
      this.page?.showNotification({ message: 'Materia cancellata.', type: 'success' })
      this.refreshTopics()
    })
  }

  onOperationClick = async (operation: operations, topicId: string) => {
    if (operation === 'Modifica') {
      navigate(topicEdit + topicId)
    }
    if (operation === 'Cancella') {
      this.page?.showConfirmDialog('Cancellazione materia', 'Vuoi davvero cancellare questo materia?', async () => {
        await TopicApi.deleteTopic(topicId)
        this.page?.showNotification({ message: 'Materia cancellata.', type: 'success' })
        this.refreshTopics()
      })
    }
  }

  syncTopics = () => {
    this.page?.showConfirmDialog(
      'Sincronizzazione di materie e argomenti',
      'Vuoi davvero sincronizzare le materie e gli argomenti?',
      async () => {
        try {
          await TopicApi.syncTopicsAndSubtopics()
          this.page?.showNotification({ message: 'Materie e Argomenti sincronizzati', type: 'info' })
        } catch (error) {
          this.page?.showError('Sincronizzazione fallita')
        }
      }
    )
  }

  render() {
    const { loading } = this.state
    const { keys, rows } = this.getKeysAndValues()
    const totalCount = Object.keys(this.topics ?? {}).length
    const buttons: ButtonProps[] = [{ title: 'Inserisci una Materia', href: addtopic }]
    if (isSuperAdmin()) buttons.push({ title: 'Sincronizza Materie', onClick: this.syncTopics })
    return (
      <Page ref={(r) => (this.page = r!)} page={pages.TOPIC_LIST}>
        {loading && <Spinner animation="border" />}
        {!loading && (
          <>
            <Header title={'Materie'} buttons={isSuperAdmin() ? buttons : []} />
            <ListFilters
              searchValue={this.state.search}
              searchPlaceholder="Nome o descrizione della materia"
              onSearchChange={(search) => this.setState({ search })}
              filters={[
                {
                  key: 'questionCountFilter',
                  label: 'Contenuto',
                  value: this.state.questionCountFilter,
                  options: [
                    { label: 'Tutte', value: 'all' },
                    { label: 'Con domande', value: 'withQuestions' },
                    { label: 'Senza domande', value: 'empty' },
                  ],
                },
              ]}
              onFilterChange={(_, questionCountFilter) => this.setState({ questionCountFilter })}
              onReset={() => this.setState({ search: '', questionCountFilter: 'all' })}
              resultCount={rows.length}
              totalCount={totalCount}
            />
            <Table keys={keys} rows={rows} onOperationClick={this.onOperationClick} />
          </>
        )}
      </Page>
    )
  }
}
